import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * TagFilled icon component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconTagFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.23919 1.10468C9.43803 1.08818 9.63805 1.11634 9.8246 1.1871C10.0079 1.25661 10.1735 1.36549 10.31 1.50602L22.3206 13.5166C22.6849 13.8814 22.8896 14.3758 22.8896 14.8914C22.8896 15.4069 22.6846 15.9017 22.3203 16.2665L16.2657 22.3211C15.9009 22.6854 15.4064 22.8901 14.8909 22.8901C14.3754 22.8901 13.8806 22.6851 13.5158 22.3208L1.50553 10.3105C1.36501 10.174 1.25612 10.0083 1.18661 9.82508C1.11585 9.63853 1.08769 9.43851 1.1042 9.23967C1.1057 9.22157 1.10784 9.20353 1.1106 9.18557L2.02417 3.25513C2.05194 2.93864 2.19022 2.64141 2.41557 2.41606C2.64093 2.19071 2.93816 2.05242 3.25464 2.02466L9.18509 1.11109C9.20305 1.10832 9.22108 1.10619 9.23919 1.10468ZM7.32696 8.88418C8.18652 8.88418 8.88332 8.18738 8.88332 7.32782C8.88332 6.46827 8.18652 5.77146 7.32696 5.77146C6.46741 5.77146 5.7706 6.46827 5.7706 7.32782C5.7706 8.18738 6.46741 8.88418 7.32696 8.88418Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-tag-filled') === undefined) {
  customElements.define('cat-icon-tag-filled', BlocksIconTagFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-tag-filled': BlocksIconTagFilled;
  }
}
